
import {
  Home,
  BarChart3,
  Users,
  Package,
  Truck,
  Factory,
  Wheat,
  DollarSign,
  Warehouse,
  FileText,
  Settings,
  Bell,
  Sparkles,
  CheckCircle,
  XCircle,
  Calendar,
  CreditCard,
  UserCheck,
  Database,
  TrendingUp,
  ShoppingCart,
  Activity,
  RefreshCw,
  Target,
  Brain,
  ClipboardCheck,
  Clock,
  History,
  Building2,
  ArrowLeftRight,
  Wallet,
  Upload,
  AlertTriangle,
  Boxes,
  Scale,
  ShieldCheck,
  Share2,
  Plus
} from 'lucide-react';
import { LucideIcon } from 'lucide-react';

export interface RouteConfig {
  name: string;
  path?: string;
  icon: LucideIcon;
  roles?: string[];
  children?: RouteConfig[];
  dynamicVisibility?: 'approver'; // Special flag for dynamic visibility
  alwaysVisible?: boolean; // If true, shown to every authenticated user regardless of role
  badgeCount?: number; // Optional badge count for nav items
}

export const routeConfig: RouteConfig[] = [
  {
    name: 'Dashboard',
    path: '/dashboard',
    icon: Home,
  },
  {
    name: 'My Approvals',
    path: '/my-approvals',
    icon: ClipboardCheck,
    dynamicVisibility: 'approver',
    children: [
      {
        name: 'Pending',
        path: '/my-approvals',
        icon: Clock,
      },
      {
        name: 'History',
        path: '/my-approvals/history',
        icon: History,
      },
    ],
  },
  {
    name: 'My Requests',
    path: '/my-requests',
    icon: FileText,
    alwaysVisible: true,
    children: [
      {
        name: 'All Requests',
        path: '/my-requests',
        icon: FileText,
      },
      {
        name: 'New Request',
        path: '/my-requests/new',
        icon: Plus,
      },
    ],
  },
  {
    name: 'Chippie AI',
    path: '/chippie-ai',
    icon: Sparkles,
    roles: ['admin', 'finance', 'warehouse', 'seller', 'benchmark_manager', 'benchmark', 'coordinator', 'quality'],
  },
  {
    name: 'Knowledge Base',
    path: '/knowledge-base',
    icon: Database,
    roles: ['admin'],
  },
  {
    name: 'Procurement',
    path: '/procurement',
    icon: Package,
    roles: ['admin', 'seller', 'finance', 'warehouse'],
  },
  {
    name: 'Coordinators',
    path: '/coordinators',
    icon: UserCheck,
    roles: ['admin', 'coordinator'],
    children: [
      {
        name: 'Dashboard',
        path: '/coordinators',
        icon: Home,
      },
      {
        name: 'Farmers',
        path: '/coordinators/farmers',
        icon: Users,
      },
      {
        name: 'Harvests',
        path: '/coordinators/harvests',
        icon: Wheat,
      },
    ],
  },
  {
    name: 'Farms',
    path: '/farms',
    icon: Wheat,
    roles: ['admin', 'seller', 'coordinator'],
  },
  {
    name: 'Harvests',
    path: '/harvests',
    icon: Factory,
    roles: ['admin', 'seller', 'coordinator'],
  },
  {
    name: 'Suppliers',
    path: '/suppliers',
    icon: Truck,
    roles: ['admin', 'seller', 'finance'],
    children: [
      {
        name: 'All Suppliers',
        path: '/suppliers',
        icon: Truck,
      },
      {
        name: 'Source Report',
        path: '/suppliers/report',
        icon: BarChart3,
      },
      {
        name: 'Supplier Governance',
        path: '/supplier-governance',
        icon: ClipboardCheck,
      },
      {
        name: 'Quality Intelligence',
        path: '/quality-intelligence',
        icon: Activity,
      },
    ],
  },
  {
    name: 'Agents',
    path: '/agents',
    icon: Users,
    roles: ['admin', 'seller', 'finance'],
  },
  {
    name: 'Price Benchmarking',
    path: '/price-benchmarking',
    icon: BarChart3,
    roles: ['benchmark_manager', 'admin', 'seller'],
    children: [
      {
        name: 'Overview',
        path: '/price-benchmarking/overview',
        icon: BarChart3,
      },
      {
        name: 'Benchmark',
        path: '/price-benchmarking/benchmark',
        icon: BarChart3,
      },
      {
        name: 'Farm Prices',
        path: '/price-benchmarking/farm-prices',
        icon: Wheat,
      },
      {
        name: 'Distribution Center',
        path: '/price-benchmarking/distribution-center',
        icon: Warehouse,
      },
      {
        name: 'Local Shops',
        path: '/price-benchmarking/local-shops',
        icon: Factory,
      },
      {
        name: 'Sunday Market',
        path: '/price-benchmarking/sunday-market',
        icon: Package,
      },
      {
        name: 'Supermarket',
        path: '/price-benchmarking/supermarket',
        icon: Package,
      },
      {
        name: 'Ecommerce',
        path: '/price-benchmarking/ecommerce',
        icon: Package,
      },
      {
        name: 'B2B Products',
        path: '/price-benchmarking/b2b-products',
        icon: Package,
      },
      {
        name: 'Multi-Product Comparison',
        path: '/price-benchmarking/multi-product-comparison',
        icon: BarChart3,
      },
      {
        name: 'Product Rankings',
        icon: TrendingUp,
        roles: ['benchmark_manager', 'admin', 'seller'],
        children: [
          {
            name: 'Distribution Center',
            path: '/price-benchmarking/rankings-distribution-center',
            icon: Warehouse,
          },
          {
            name: 'Supermarket',
            path: '/price-benchmarking/rankings-supermarket',
            icon: ShoppingCart,
          },
          {
            name: 'Overall Market',
            path: '/price-benchmarking/rankings-overall',
            icon: BarChart3,
          },
          {
            name: 'Consistency',
            path: '/price-benchmarking/rankings-consistency',
            icon: Activity,
          },
          {
            name: 'Volume Opportunity',
            path: '/price-benchmarking/rankings-volume',
            icon: Target,
          },
        ],
      },
      {
        name: 'Data Entry',
        path: '/price-benchmarking/data-entry',
        icon: FileText,
        roles: ['benchmark', 'benchmark_manager'],
      },
      {
        name: 'Manage Data',
        path: '/price-benchmarking/manage-data',
        icon: Database,
        roles: ['benchmark_manager', 'admin'],
      },
      {
        name: 'Manage Products',
        path: '/price-benchmarking/manage-products',
        icon: Package,
        roles: ['benchmark_manager', 'admin'],
      },
      {
        name: 'Report',
        path: '/price-benchmarking/report',
        icon: BarChart3,
        roles: ['benchmark_manager', 'admin'],
      },
    ],
  },
  {
    name: 'Price Forecast',
    path: '/price-forecast',
    icon: Brain,
    roles: ['admin', 'benchmark_manager', 'seller'],
  },
  {
    name: 'Finance',
    path: '/finance',
    icon: DollarSign,
    roles: ['admin', 'finance'],
    children: [
      {
        name: 'Procurements',
        icon: Package,
        children: [
          {
            name: 'Pending Approval',
            path: '/finance/approvals/pending',
            icon: Clock,
          },
          {
            name: 'Approved',
            path: '/finance/approvals/approved',
            icon: CheckCircle,
          },
          {
            name: 'Rejected',
            path: '/finance/approvals/rejected',
            icon: XCircle,
          },
          {
            name: 'Payment Queue',
            path: '/finance/payments/confirmation',
            icon: CreditCard,
          },
          {
            name: 'Paid',
            path: '/finance/payments/confirmed',
            icon: CheckCircle,
          },
          {
            name: 'Discrepancies',
            path: '/finance/payments/discrepancies',
            icon: AlertTriangle,
          },
        ],
      },
      {
        name: 'Payment Requests',
        icon: FileText,
        children: [
          {
            name: 'All Requests',
            path: '/finance/accounting/payment-requests',
            icon: FileText,
          },
          {
            name: 'Payment Queue',
            path: '/finance/payments/payment-requests',
            icon: CreditCard,
          },
        ],
      },
      {
        name: 'Banking',
        icon: Building2,
        children: [
          {
            name: 'Transactions',
            path: '/finance/banking/transactions',
            icon: ArrowLeftRight,
          },
          {
            name: 'Balances',
            path: '/finance/banking/balances',
            icon: Wallet,
          },
          {
            name: 'Reconciliation',
            path: '/finance/banking/reconciliation',
            icon: Scale,
          },
          {
            name: 'Import History',
            path: '/finance/banking/imports',
            icon: Upload,
          },
          {
            name: 'Internal Transfers',
            path: '/finance/transfers',
            icon: ArrowLeftRight,
          },
        ],
      },
      {
        name: 'Accounting',
        icon: DollarSign,
        children: [
          {
            name: 'Expenses',
            path: '/finance/accounting/expenses',
            icon: DollarSign,
          },
          {
            name: 'Subscriptions',
            path: '/finance/subscriptions',
            icon: RefreshCw,
          },
          {
            name: 'GRN',
            path: '/finance/accounting/grn',
            icon: Package,
          },
          {
            name: 'Cash Inflows',
            path: '/finance/accounting/cash-inflows',
            icon: TrendingUp,
          },
        ],
      },
      {
        name: 'Schedule',
        icon: Calendar,
        children: [
          {
            name: 'Upcoming Payments',
            path: '/finance/payments/upcoming',
            icon: Clock,
          },
          {
            name: 'Payment Calendar',
            path: '/finance/payments/calendar',
            icon: Calendar,
          },
          {
            name: 'Recurring Payments',
            path: '/finance/recurring-payments',
            icon: RefreshCw,
          },
        ],
      },
      {
        name: 'Analytics',
        icon: BarChart3,
        children: [
          {
            name: 'Reports',
            path: '/finance/reports',
            icon: BarChart3,
          },
          {
            name: 'Cash Flow',
            path: '/finance/reports/cash-flow',
            icon: DollarSign,
          },
          {
            name: 'Cash Flow New',
            path: '/finance/reports/cash-flow-new',
            icon: DollarSign,
          },
          {
            name: 'Cash Flow',
            path: '/finance/reports/financial-report',
            icon: DollarSign,
          },
          {
            name: 'P&L Report',
            path: '/finance/reports/pnl-report',
            icon: DollarSign,
          },
          {
            name: 'Balance Sheet',
            path: '/finance/reports/balance-sheet',
            icon: Scale,
          },
          {
            name: 'Payment Speed KPI',
            path: '/finance/reports/payment-speed',
            icon: Activity,
          },
          {
            name: 'Investor Snapshot',
            path: '/finance/reports/investor-snapshot',
            icon: Share2,
          },
          {
            name: 'Cash Flow Details',
            path: '/finance/reports/cash-flow-details',
            icon: DollarSign,
          },
          {
            name: 'Spend Analysis',
            path: '/finance/spend-analysis',
            icon: TrendingUp,
          },
          {
            name: 'Cash Outflow',
            path: '/finance/reports/cash-outflow',
            icon: Activity,
          },
          {
            name: 'Department Costs',
            path: '/finance/reports/department-costs',
            icon: Building2,
          },
          {
            name: 'Business Unit P&L',
            path: '/finance/reports/business-unit-pnl',
            icon: Building2,
          },
        ],
      },
      {
        name: 'Settings',
        icon: Settings,
        children: [
          {
            name: 'Departments',
            path: '/finance/settings/departments',
            icon: Settings,
          },
          {
            name: 'Approvers',
            path: '/finance/settings/approvers',
            icon: UserCheck,
          },
          {
            name: 'Categories',
            path: '/finance/settings/categories',
            icon: FileText,
          },
          {
            name: 'Cost Positions',
            path: '/finance/settings/cost-positions',
            icon: FileText,
          },
          {
            name: 'Bank Accounts',
            path: '/finance/settings/bank-accounts',
            icon: CreditCard,
          },
          {
            name: 'Tax Settings',
            path: '/finance/settings/tax',
            icon: DollarSign,
          },
          {
            name: 'Exchange Rates',
            path: '/finance/settings/exchange-rates',
            icon: ArrowLeftRight,
          },
          {
            name: 'Business Unit Assignments',
            path: '/finance/settings/business-units',
            icon: FileText,
          },
          {
            name: 'Employee BU Allocations',
            path: '/finance/settings/employee-allocations',
            icon: FileText,
          },
        ],
      },
    ],
  },
  {
    name: 'Quality',
    path: '/quality',
    icon: ClipboardCheck,
    roles: ['admin', 'quality'],
  },
  {
    name: 'Warehouse',
    path: '/warehouse',
    icon: Warehouse,
    roles: ['admin', 'warehouse'],
  },
  {
    name: 'Inventory',
    path: '/inventory',
    icon: Boxes,
    roles: ['admin', 'warehouse', 'quality', 'finance'],
    children: [
      {
        name: 'Stock Levels',
        path: '/inventory',
        icon: Package,
      },
      {
        name: 'Transactions',
        path: '/inventory/transactions',
        icon: ArrowLeftRight,
      },
      {
        name: 'Daily Count',
        path: '/inventory/stock-count',
        icon: ClipboardCheck,
      },
      {
        name: 'Damages',
        path: '/inventory/damages',
        icon: AlertTriangle,
      },
      {
        name: 'Sync Report',
        path: '/inventory/sync-report',
        icon: RefreshCw,
      },
      {
        name: 'Unmatched Products',
        path: '/inventory/unmatched-products',
        icon: AlertTriangle,
      },
    ],
  },
  {
    name: 'Reports',
    path: '/reports',
    icon: FileText,
    roles: ['admin', 'quality', 'warehouse', 'finance', 'seller'],
    children: [
      {
        name: 'All Reports',
        path: '/reports',
        icon: FileText,
      },
      {
        name: 'Procurement Damage',
        path: '/reports/procurement-damage',
        icon: AlertTriangle,
      },
      {
        name: 'Batch Traceability',
        path: '/reports/batch-traceability',
        icon: Boxes,
      },
      {
        name: 'Supplier Underwriting',
        path: '/reports/underwriting',
        icon: ShieldCheck,
      },
      {
        name: 'ChipChip Sales Data',
        path: '/price-benchmarking/chipchip-sales-data',
        icon: ShoppingCart,
      },
      {
        name: 'Orders / Quantity / GMV',
        path: '/price-benchmarking/chipchip-accepted-daily',
        icon: ShoppingCart,
      },
      {
        name: 'B2B Sales Data',
        path: '/price-benchmarking/b2b-sales-data',
        icon: TrendingUp,
      },
      {
        name: 'B2B Credit Collections',
        path: '/price-benchmarking/b2b-credit-collections',
        icon: TrendingUp,
      },
      {
        name: 'Zarely Report',
        path: '/price-benchmarking/zarely-report',
        icon: ShoppingCart,
      },
    ],
  },
  {
    name: 'Users',
    path: '/users',
    icon: Users,
    roles: ['admin', 'finance'],
  },
  {
    name: 'Settings',
    path: '/settings',
    icon: Settings,
    roles: ['admin'],
  },
];
